/**
 * A single, branded address search surface for flows that need a precise
 * Salvadoran place.  Tours, Services and future pickup flows should not each
 * grow their own debounce, keyboard and result-row behaviour.
 */
import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { MapPin, Search } from '@/components/ui/icons'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { LiquidCanvas } from '@/components/ui/LiquidBackdrop'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { TextInput } from '@/components/ui/TextInput'
import { useTheme } from '@/lib/hooks/use-theme'
import { geocodingService, type GeocodingResult } from '@/lib/services/geocoding.service'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'

export interface SearchLocation {
  label: string
  latitude: number
  longitude: number
  /** Nombre del POI cuando Mapbox lo reconoce (p. ej. "Multiplaza"). */
  name?: string | null
  /** Dirección completa; la línea que necesita el conductor. */
  address?: string | null
}

function locationFromGeocode(result: GeocodingResult): SearchLocation {
  return {
    label: result.address,
    latitude: result.lat,
    longitude: result.lng,
    name: result.name,
    address: result.address,
  }
}

/** Primera línea de un resultado: el POI manda cuando aporta contexto. */
function primaryResultLabel(result: GeocodingResult): string {
  return result.name && result.name !== result.address ? result.name : result.address
}

/** Segunda línea: la dirección completa si el POI la encabeza, si no la ciudad. */
function secondaryResultLabel(result: GeocodingResult): string | null {
  if (result.name && result.name !== result.address) {
    return result.address || result.city
  }
  return result.city
}

interface LocationSearchModalProps {
  visible: boolean
  title: string
  onClose: () => void
  onPick: (location: SearchLocation) => void
  /** Stable prefix keeps each flow's E2E contract without duplicating UI. */
  testIDPrefix: string
  placeholder: string
  searchingLabel: string
  emptyLabel: string
  errorLabel: string
  /**
   * Lugares de referencia listos para elegir (malls, monumentos, aeropuerto…).
   * Con el campo vacío se ofrecen como chips y un toque SELECCIONA el lugar:
   * son coordenadas verificadas, no consultas — el geocoder de Mapbox no
   * indexa los landmarks salvadoreños y una búsqueda por texto los convierte
   * en plazas parecidas.
   */
  quickPicks?: readonly SearchLocation[]
  quickPicksLabel?: string
}

export function LocationSearchModal({
  visible,
  title,
  onClose,
  onPick,
  testIDPrefix,
  placeholder,
  searchingLabel,
  emptyLabel,
  errorLabel,
  quickPicks,
  quickPicksLabel,
}: LocationSearchModalProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeocodingResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!visible) {
      setQuery('')
      setResults([])
      setError(null)
      setIsSearching(false)
    }
  }, [visible])

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setResults([])
      setError(null)
      setIsSearching(false)
      return undefined
    }

    let active = true
    const timeout = setTimeout(() => {
      setIsSearching(true)
      setError(null)
      void geocodingService
        .forward(trimmed)
        .then((next) => {
          if (active) setResults(next)
        })
        .catch(() => {
          if (active) setError(errorLabel)
        })
        .finally(() => {
          if (active) setIsSearching(false)
        })
    }, 280)

    return () => {
      active = false
      clearTimeout(timeout)
    }
  }, [errorLabel, query])

  return (
    <AppModal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <LiquidCanvas isolate style={{ paddingTop: SPACING.lg }}>
        <ScreenHeader title={title} showBack onBackPress={onClose} />
        <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.md, flex: 1 }}>
          <TextInput
            value={query}
            onChangeText={setQuery}
            autoFocus
            autoCorrect={false}
            autoCapitalize="words"
            returnKeyType="search"
            placeholder={placeholder}
            leftIcon={<Search size={20} />}
            accessibilityLabel={placeholder}
            testID={`${testIDPrefix}-search`}
          />
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              paddingTop: SPACING.xs,
              paddingBottom: SPACING['2xl'],
              gap: SPACING.xs,
            }}
            showsVerticalScrollIndicator={false}
          >
            {!isSearching && !error && query.trim().length < 2 ? (
              <GlassSurface
                tone="quiet"
                radius={RADIUS.lg}
                contentStyle={{
                  padding: SPACING.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.sm,
                }}
              >
                <View
                  style={{
                    width: INTERACTION.minTouchTarget,
                    height: INTERACTION.minTouchTarget,
                    borderRadius: RADIUS.full,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(239,78,74,0.10)',
                  }}
                >
                  <MapPin size={20} color={COLORS.coral.DEFAULT} strokeWidth={2.1} />
                </View>
                <Text
                  style={{
                    flex: 1,
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    color: colors.textSecondary,
                  }}
                >
                  {placeholder}
                </Text>
              </GlassSurface>
            ) : null}
            {!isSearching && !error && query.trim().length < 2 && quickPicks?.length ? (
              <View style={{ marginTop: SPACING.sm }} testID={`${testIDPrefix}-quick-list`}>
                {quickPicksLabel ? (
                  <Text
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 13,
                      color: colors.textMuted,
                    }}
                  >
                    {quickPicksLabel}
                  </Text>
                ) : null}
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    marginTop: SPACING.xs,
                    gap: SPACING.xs,
                  }}
                >
                  {quickPicks.map((place, index) => (
                    <Pressable
                      key={`${place.latitude}:${place.longitude}:${place.label}`}
                      onPress={() => onPick(place)}
                      accessibilityRole="button"
                      accessibilityLabel={place.label}
                      testID={`${testIDPrefix}-quick-${index}`}
                      style={({ pressed }) => ({ opacity: pressed ? 0.76 : 1 })}
                    >
                      <GlassSurface
                        tone="regular"
                        radius={RADIUS.full}
                        style={{ minHeight: INTERACTION.minTouchTarget }}
                        contentStyle={{
                          paddingHorizontal: SPACING.md,
                          paddingVertical: SPACING.xs,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Text
                          style={{
                            fontFamily: FONTS.body,
                            fontSize: 13,
                            color: colors.textPrimary,
                          }}
                        >
                          {place.label}
                        </Text>
                      </GlassSurface>
                    </Pressable>
                  ))}
                </View>
              </View>
            ) : null}
            {isSearching ? (
              <GlassSurface tone="quiet" radius={RADIUS.lg} contentStyle={{ padding: SPACING.md }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
                  {searchingLabel}
                </Text>
              </GlassSurface>
            ) : null}
            {error ? (
              <GlassSurface tone="quiet" radius={RADIUS.lg} contentStyle={{ padding: SPACING.md }}>
                <Text
                  accessibilityRole="alert"
                  style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textError }}
                >
                  {error}
                </Text>
              </GlassSurface>
            ) : null}
            {!isSearching && !error && query.trim().length >= 2 && results.length === 0 ? (
              <GlassSurface tone="quiet" radius={RADIUS.lg} contentStyle={{ padding: SPACING.md }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
                  {emptyLabel}
                </Text>
              </GlassSurface>
            ) : null}
            {results.map((result, index) => {
              const primary = primaryResultLabel(result)
              const secondary = secondaryResultLabel(result)
              return (
                <Pressable
                  key={`${result.lat}:${result.lng}:${result.address}`}
                  onPress={() => onPick(locationFromGeocode(result))}
                  accessibilityRole="button"
                  accessibilityLabel={primary}
                  testID={`${testIDPrefix}-${index}`}
                  style={({ pressed }) => ({ opacity: pressed ? 0.76 : 1 })}
                >
                  <GlassSurface
                    tone="regular"
                    radius={RADIUS.md}
                    style={{ minHeight: INTERACTION.minTouchTarget }}
                    contentStyle={{
                      padding: SPACING.md,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.sm,
                    }}
                  >
                    <MapPin size={20} color={COLORS.coral.DEFAULT} strokeWidth={2.1} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        numberOfLines={2}
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 14,
                          color: colors.textPrimary,
                        }}
                      >
                        {primary}
                      </Text>
                      {secondary ? (
                        <Text
                          numberOfLines={2}
                          style={{
                            marginTop: 2,
                            fontFamily: FONTS.body,
                            fontSize: 12,
                            color: colors.textSecondary,
                          }}
                        >
                          {secondary}
                        </Text>
                      ) : null}
                    </View>
                  </GlassSurface>
                </Pressable>
              )
            })}
          </ScrollView>
        </View>
      </LiquidCanvas>
    </AppModal>
  )
}
